import { pickedDay } from "@/lib/clinic-clock";
import { overlapsRange, toMs } from "@/lib/timestamp";
import {
  clockKey,
  isValidTimezone,
  locationTimezone,
  locationsSharedTimezone,
} from "@/lib/timezone";
import { zonedDateKey, zonedDayEnd, zonedDayStart } from "@/lib/timezone-dates";

import { EventType } from "@/types/enums/event";
import { EventTiny } from "@/types/event";

/**
 * Which clock each part of the calendar is drawn on.
 *
 * A clinic whose locations sit in different timezones books each location on
 * its own clock: a Florida 9:00 and a Colorado 9:00 are both the first slot of
 * the day. Drawing every event on the viewer's clock would put one of them two
 * hours off, so with `localTimes` on, every event sits at its own location's
 * wall time and every column reads its times on the clock of where that column
 * is working. Off, every answer is the viewer's zone — the calendar as it was.
 */
export interface CalendarClocks {
  /** Whether each location is drawn on its own clock. */
  localTimes: boolean;
  /** The viewer's own zone (`useTimezone()`). */
  viewerZone: string;
  /**
   * The clock of a column that isn't one provider's working day — a resource
   * column, or a week day showing several providers: the clock the selected
   * locations share, else the viewer's.
   */
  gridZone: string;
  /** Whether `gridZone` is a clock every location in view keeps. */
  gridShared: boolean;
  /**
   * The clock an event is drawn on: its location's. `undefined` when it has no
   * clock of its own — no location, a virtual one, or `localTimes` off — so it
   * takes the column's.
   */
  zoneOfEvent: (event: Pick<EventTiny, "location">) => string | undefined;
  /** A location's clock, `undefined` as for `zoneOfEvent`. */
  zoneOfLocation: (locationId: string | null | undefined) => string | undefined;
  /**
   * The clock of a provider's column on `day` (a calendar-day marker): the
   * location they work at that day — their first shift, else their first
   * appointment — else where they work on another day in view, else
   * `gridZone`.
   */
  zoneOfProviderDay: (providerId: string, day: Date) => string;
  /** Every clock the view can be drawn on, for the fetch window. */
  zones: string[];
}

interface ClockLocation {
  id: string;
  timezone?: string | null;
  isVirtual?: boolean;
  isArchived?: boolean;
}

/** Calendar clocks that answer the viewer's zone everywhere. */
export function viewerCalendarClocks(viewerZone: string): CalendarClocks {
  return {
    localTimes: false,
    viewerZone,
    gridZone: viewerZone,
    gridShared: true,
    zoneOfEvent: () => undefined,
    zoneOfLocation: () => undefined,
    zoneOfProviderDay: () => viewerZone,
    zones: [viewerZone],
  };
}

export function buildCalendarClocks({
  localTimes,
  viewerZone,
  tenantDefaultTimezone,
  locations,
  selectedLocationIds,
  events,
}: {
  localTimes: boolean;
  viewerZone: string;
  tenantDefaultTimezone: string | null | undefined;
  /** Every location, archived ones included: a record keeps its clock. */
  locations: readonly ClockLocation[];
  selectedLocationIds: readonly string[];
  /** The fetched window, unfiltered: where each provider works each day. */
  events: readonly EventTiny[];
}): CalendarClocks {
  if (!localTimes) return viewerCalendarClocks(viewerZone);

  const locationsById = new Map(locations.map((l) => [l.id, l]));
  const zoneOfLocation = (locationId: string | null | undefined) => {
    const location = locationId ? locationsById.get(locationId) : undefined;
    // A virtual visit has no clinic clock of its own.
    if (!location || location.isVirtual) return undefined;
    const zone = locationTimezone(location, tenantDefaultTimezone, "");
    return isValidTimezone(zone) ? zone : undefined;
  };
  const zoneOfEvent = (event: Pick<EventTiny, "location">) =>
    zoneOfLocation(event.location?.id);

  const inPerson = locations.filter((l) => !l.isVirtual);
  const selectedInPerson = selectedLocationIds.filter(
    (id) => locationsById.get(id) && !locationsById.get(id)?.isVirtual,
  );
  const gridZone = locationsSharedTimezone(
    selectedInPerson,
    inPerson,
    tenantDefaultTimezone,
    viewerZone,
  );

  // One clock per distinct offset schedule, so the fetch window doesn't grow
  // for two names of one clock.
  const viewIds = selectedInPerson.length
    ? selectedInPerson
    : inPerson.filter((l) => !l.isArchived).map((l) => l.id);
  const viewZones = new Map<string, string>();
  for (const id of viewIds) {
    const zone = zoneOfLocation(id);
    if (zone && !viewZones.has(clockKey(zone))) {
      viewZones.set(clockKey(zone), zone);
    }
  }
  const zonesByKey = new Map<string, string>([
    [clockKey(viewerZone), viewerZone],
    ...viewZones,
  ]);

  // Where each provider works each day: their earliest shift, else their
  // earliest appointment, on the day that event falls on at its location.
  // Keyed by provider alone too, for a day they aren't working: their column
  // still reads on the clock of where they work, not the viewer's.
  type Workplace = { zone: string; shift: boolean; startMs: number };
  const workplaces = new Map<string, Workplace>();
  const prefer = (known: Workplace | undefined, next: Workplace) =>
    !known ||
    (next.shift && !known.shift) ||
    (next.shift === known.shift && next.startMs < known.startMs);
  for (const event of events) {
    const shift = event.type === EventType.SHIFT;
    if (!shift && event.type !== EventType.APPOINTMENT) continue;
    const zone = zoneOfEvent(event);
    if (!zone) continue;
    const key = zonedDateKey(event.start, zone);
    const startMs = toMs(event.start);
    const workplace = { zone, shift, startMs };
    for (const attendee of event.attendees) {
      for (const id of [`${attendee.user.id}|${key}`, attendee.user.id]) {
        if (prefer(workplaces.get(id), workplace)) {
          workplaces.set(id, workplace);
        }
      }
    }
  }

  return {
    localTimes,
    viewerZone,
    gridZone,
    gridShared: viewZones.size <= 1,
    zoneOfEvent,
    zoneOfLocation,
    zoneOfProviderDay: (providerId, day) =>
      (
        workplaces.get(`${providerId}|${pickedDay(day)}`) ??
        workplaces.get(providerId)
      )?.zone ?? gridZone,
    zones: Array.from(zonesByKey.values()),
  };
}

/** `day`'s (a calendar-day marker) window on each clock, built once per zone. */
export function dayWindows(day: Date) {
  const cache = new Map<string, { start: Date; end: Date }>();
  return (zone: string) => {
    let window = cache.get(zone);
    if (!window) {
      window = { start: zonedDayStart(day, zone), end: zonedDayEnd(day, zone) };
      cache.set(zone, window);
    }
    return window;
  };
}

/**
 * Whether an event falls on `day` as its own clock counts days —
 * `columnZone`'s when it has none.
 */
export function eventsOnDay(
  day: Date,
  columnZone: string,
  zoneOfEvent: CalendarClocks["zoneOfEvent"],
) {
  const windowOf = dayWindows(day);
  return (event: EventTiny) => {
    const { start, end } = windowOf(zoneOfEvent(event) ?? columnZone);
    return overlapsRange(event, start, end);
  };
}

/** Whether `event` starts on `day` on its own clock (`columnZone`'s without one). */
export function eventsStartingOnDay(
  day: Date,
  columnZone: string,
  zoneOfEvent: CalendarClocks["zoneOfEvent"],
) {
  const windowOf = dayWindows(day);
  return (event: EventTiny) => {
    const { start, end } = windowOf(zoneOfEvent(event) ?? columnZone);
    const startMs = toMs(event.start);
    return startMs >= start.getTime() && startMs <= end.getTime();
  };
}
